import React, { useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { annotation, annotationLabel } from 'd3-svg-annotation';
import { MATURITY_TRANSFORMATION_ID } from '../../types';
import { MATURITY_PLAN_OPTIONS } from '../../constants/maturity-plans';
import { useCustomer } from '../../context/CustomerContext';

interface TransformationMaturityItem {
  step_id: string;
  transformation_area: string;
  priority?: string;
  growth: string;
  notes: string;
  xaxis: string | number;
  yaxis: string | number;
  label_xaxis: string | number;
  label_yaxis: string | number;
  order?: number;
}

interface TransformationMaturityTabProps {
  customerId?: string;
  /** When set, overrides internal selection for presentation mode */
  selectedPlanId?: string;
  /** When true, hides the "Choose A Plan" dropdown */
  hideDropdown?: boolean;
  /** When set, use instead of "Maturity Journey" for the h2 title */
  customTitle?: string;
  /** When true, hide the static description paragraph */
  hideDescription?: boolean;
  /** When true, reduce padding/margins for compact presentation view */
  compactPresentation?: boolean;
  /** Optional action to render on the header row (e.g. Download as PNG button) */
  headerAction?: React.ReactNode;
}

// Color mapping for growth phases - intuitive and pronounced
const growthStatusColors = {
  'Not Started': '#F59E0B',    // amber-500
  'In Progress': '#3B82F6',    // blue-500
  'In Production': '#10B981',  // emerald-500
  'No Plans': '#EF4444',       // red-500
  'UnAssigned': '#6B7280',     // gray-500
  'NotStarted': '#F59E0B',
  'NoPlan': '#EF4444',
  'InProgress': '#3B82F6',
  'InProduction': '#10B981',
  'Select': '#9CA3AF',         // Default gray for "Select" option
  'Default': '#3B82F6',        // Blue by default
};

// Legend data matching Vue component
const yAxisBoxData = [
  { color: "#4CAF50", label: "IT Empowering the business to innovate" },
  { color: "#005BA1", label: "Efficient IT environment to enable business agility" },
  { color: "#0078D4", label: "IT Provides optimized tools and methods" },
  { color: "#A5D6A7", label: "IT perceived as an operational cost center" },
  { color: "white", label: "" }
];

// X-axis boxes data matching Vue component
const xAxisBoxData = [
  { text: "Legacy/Technical Debt", percentage: 0.20, color: "#D9D9D9", tcolor: "black" },
  { text: "Conventional", percentage: 0.35, color: "#0B3D1A", tcolor: "white" },
  { text: "Modernized / Optimized", percentage: 0.45, color: "#A5D66A", tcolor: "white" }
];

const TransformationMaturityGraph: React.FC<TransformationMaturityTabProps> = ({
  customerId,
  selectedPlanId,
  hideDropdown = false,
  customTitle,
  hideDescription = false,
  compactPresentation = false,
  headerAction,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalSelectedPlan, setInternalSelectedPlan] = useState<string>(MATURITY_TRANSFORMATION_ID.NO_MATURITY);
  const effectivePlanId = selectedPlanId ?? internalSelectedPlan;
  const [containerWidth, setContainerWidth] = useState<number>(1200);
  const { customerData, dashboardLoading } = useCustomer();

  // Derive maturity levels from context (dashboard API data)
  const filteredMaturityLevels = useMemo(() => {
    if (effectivePlanId === MATURITY_TRANSFORMATION_ID.NO_MATURITY) {
      return [];
    }
    const rawLevels = customerData?.maturity_transformations_by_plan?.[effectivePlanId]?.maturity_levels ?? [];
    // Convert string coordinates to numbers for D3, sort by order
    const processedLevels: TransformationMaturityItem[] = rawLevels.map((level) => ({
      ...level,
      xaxis: Number(level.xaxis),
      yaxis: Number(level.yaxis),
      label_xaxis: level.label_xaxis ?? level.xaxis,
      label_yaxis: level.label_yaxis ?? level.yaxis,
    }));
    return processedLevels.sort((a, b) =>
      (a.order !== undefined && b.order !== undefined)
        ? a.order - b.order
        : (Number(a.xaxis) || 0) - (Number(b.xaxis) || 0)
    );
  }, [customerData?.maturity_transformations_by_plan, effectivePlanId]);

  useEffect(() => {
    if (!svgRef.current || filteredMaturityLevels.length === 0) {
      const svgElement = svgRef.current;
      if (svgElement) {
        d3.select(svgElement).selectAll("*").remove();
      }
      return;
    }

    const svgElement = svgRef.current;
    if (!svgElement) return;

    // Clear previous chart contents
    d3.select(svgElement).selectAll("*").remove();

    // Calculate width based on container - content must fit within container
    // Get the actual available width from the container ref or parent element
    let availableWidth = containerWidth;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      availableWidth = rect.width || containerWidth;
    } else {
      // Fallback to parent element if containerRef not available yet
      const containerElement = svgElement.parentElement?.parentElement;
      if (containerElement) {
        const containerRect = containerElement.getBoundingClientRect();
        availableWidth = containerRect.width || containerWidth;
      }
    }
    
    const xAxisBoxHeight = 40;
    const xAxisBoxGap = 6;
    const maxPotentialAnnotationDownshift = 60;
    const bottomPadding = 15;

    const margin = { 
      top: 30,
      right: 25,
      bottom: maxPotentialAnnotationDownshift + xAxisBoxGap + xAxisBoxHeight + bottomPadding, 
      left: 110
    };

    // Use the container width - content must fit within it
    const width = availableWidth;
    const height = 640;

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgElement)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Define X scale using corrected coordinates to match reference image
    const xScale = d3.scaleLinear()
      .domain([0, 1400]) // Adjusted to match the new coordinate system
      .range([20, chartWidth - 50]); // Range now accommodates full content

    // Define Y scale using corrected coordinates to match reference image
    const yScale = d3.scaleLinear()
      .domain([0, 500]) // Adjusted to match the new coordinate system
      .range([chartHeight - 30, 10]); // Adjusted range

    // Define the line generator with better curve for journey pattern
    const lineGenerator = d3.line<TransformationMaturityItem>()
      .x(d => xScale(Number(d.xaxis) || 0))
      .y(d => yScale(Number(d.yaxis) || 0))
      .curve(d3.curveCatmullRom.alpha(0.7)); // Better curve for the journey pattern

    // Add the Y-axis legend (colored squares and labels) - make it more compact
    const legendSquareWidth = 120; // Increased width to accommodate text better
    const legendSquareHeight = Math.max(30, chartHeight / yAxisBoxData.length); // Ensure minimum height

    const legend = svg.append("g")
      .attr("class", "legend")
      .attr("transform", `translate(${-margin.left + 5}, ${margin.top + 5})`);

    const legendItems = legend.selectAll(".legend-item")
      .data(yAxisBoxData)
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => `translate(0, ${i * legendSquareHeight})`);

    legendItems.append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", legendSquareWidth)
      .attr("height", legendSquareHeight - 2) // Further reduced height
      .style("fill", d => d.color);

    const legendPadding = 6; // Padding for left and right sides of legend boxes (reduced to allow more text space)
    const availableTextWidth = legendSquareWidth - (2 * legendPadding) - 4; // Available width for text with extra safety margin
    
    legendItems.append("text")
      .attr("x", legendPadding) // Start text from left padding instead of center
      .attr("y", (legendSquareHeight - 2) / 2)
      .attr("text-anchor", "start") // Align text to start (left) instead of middle
      .attr("dominant-baseline", "middle")
      .style("font-size", "12px") // Further reduced font size to prevent overflow
      .style("fill", d => {
        // Determine text color based on background brightness for contrast (Vue logic)
        const color = d3.color(d.color);
        if (!color) return 'black'; // Fallback for invalid colors
        // Calculate luminance (standard formula) - handle both RGB and HSL colors
        const rgb = color.rgb();
        const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
        return luminance > 0.5 ? 'black' : 'white'; // Use black text on light backgrounds, white on dark
      })
      .text(d => d.label)
      .call(wrapLegendText, availableTextWidth, legendSquareHeight - 3); // Call text wrapping function with proper width

    // Append the line path with better styling
    svg.append("path")
      .datum(filteredMaturityLevels)
      .attr("fill", "none")
      .attr("stroke", "#007BFF")
      .attr("stroke-width", 2.5)
      .attr("opacity", 0.9)
      .attr("d", lineGenerator);

    // Add circles for each data point with improved emphasis and outline
    svg.selectAll(".dot")
      .data(filteredMaturityLevels)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", d => xScale(Number(d.xaxis) || 0))
      .attr("cy", d => yScale(Number(d.yaxis) || 0))
      .attr("r", 10) // Slightly larger for better visibility
      .attr("fill", d => growthStatusColors[d.growth as keyof typeof growthStatusColors] || growthStatusColors['Default'])
      .attr("stroke", "#000000") // Thin black outline
      .attr("stroke-width", 1.2)
      .attr("opacity", 0.95);

    // Create annotations using d3-annotation with label coordinates from API
    const annotations = filteredMaturityLevels
      .filter(d => d.transformation_area && d.transformation_area.trim() !== "")
      .map((d,i) => {
        const x = Number(d.xaxis) || 0;
        const y = Number(d.yaxis) || 0;
        const xPos = xScale(x);
        const yPos = yScale(y);

        // Position labels alternating above and below the circles
        const dx = 0;
        const dy = (i % 2 === 0 ? -50 : 50);

        return {
          note: {
            label: d.transformation_area,
            bgPadding: 6,
            align: "middle",
            wrap: 26,
            fontSize: 10,
            fontFamily: "Arial, sans-serif"
          },
          data: d,
          x: xPos,
          y: yPos,
          dx: dx,
          dy: dy,
          connector: { type: "line", end: "dot" }
        };
      });

    const makeAnnotations = annotation()
      .type(annotationLabel)
      .annotations(annotations);

    svg.append("g")
      .attr("class", "annotation-group")
      .call(makeAnnotations as any);
        
    svg.selectAll(".annotation-group .annotation-note-label")
      .style("font-family", "Arial, sans-serif")
      .style("font-size", "10px")
      .style("font-weight", "600")
      .style("fill", "#1F2937");

    svg.selectAll(".annotation-group .annotation-note-bg")
      .style("fill", "#ffffff")
      .style("stroke", "#c0c0c0")
      .style("stroke-width", 1)
      .style("opacity", 0.95);

    // Style the annotation note background (the box)
    svg.selectAll(".annotation-group .annotation-note-bg")
      .style("fill", "#f9f9f9") // Light background color for the box
      .style("stroke", "#aaaaaa") // Border color for the box
      .style("stroke-width", 1)   // Border width
      .style("rx", 3)             // Rounded corners (horizontal radius)
      .style("ry", 3);            // Rounded corners (vertical radius)

    svg.append("g")
      .attr("class", "annotation-group")
      .call(makeAnnotations as any);

    makeAnnotations.update();

    // Add Custom X-Axis Boxes - more compact
    const xScaleRange = xScale.range();
    const totalXAxisWidth = xScaleRange[1] - xScaleRange[0];

    // Add Phase/Growth Status Legend as SVG (avoids html2canvas flex alignment issues on PNG export)
    const statusLegendData = [
      { phase: 'Not Started', color: growthStatusColors['Not Started'] },
      { phase: 'In Progress', color: growthStatusColors['In Progress'] },
      { phase: 'In Production', color: growthStatusColors['In Production'] },
      { phase: 'No Plans', color: growthStatusColors['No Plans'] },
      { phase: 'UnAssigned', color: growthStatusColors['UnAssigned'] }
    ];
    // commenting used variables;
    // const statusLegendItemMinWidth = 100;
    const availableLegendWidth = Math.max(180, chartWidth - 20);
    const itemsPerRow = Math.min(2, statusLegendData.length); // Force 2 columns for compactness
    const statusLegendItemWidth = Math.max(80, availableLegendWidth / itemsPerRow);
    // const statusLegendRows = Math.ceil(statusLegendData.length / itemsPerRow);
    // const statusLegendVerticalOffset = 4;

    const statusLegendGroup = svg.append("g")
      .attr("class", "status-legend-group")
      .attr("transform", `translate(-120, ${-margin.top + 6})`);
    statusLegendGroup.selectAll(".status-legend-item")
      .data(statusLegendData)
      .enter()
      .append("g")
      .attr("class", "status-legend-item")
      .attr("transform", (_, i) => {
        const row = Math.floor(i / itemsPerRow);
        const col = i % itemsPerRow;
        const baseX = col * (statusLegendItemWidth + 4);
        const adjustedX = baseX + (col === 0 ? 50 : -400);
        return `translate(${adjustedX}, ${row * 16})`;
      })
      .each(function(d) {
        const g = d3.select(this);
        g.append("circle")
          .attr("r", 5)
          .attr("cx", 0)
          .attr("cy", 0)
          .attr("fill", d.color)
          .attr("stroke", "#1F2937")
          .attr("stroke-width", 1);
        g.append("text")
          .attr("x", 12)
          .attr("y", 0)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .style("font-size", "10px")
          .style("font-weight", "600")
          .style("font-family", "Arial, sans-serif")
          .style("fill", "#111827")
          .style("letter-spacing", "0.01em")
          .text(d.phase);
      });

    // Add Custom X-Axis Boxes - more compact
    const xAxisBoxesGroup = svg.append("g")
      .attr("class", "x-axis-boxes-group")
      .attr("transform", `translate(${xScaleRange[0]}, ${chartHeight + maxPotentialAnnotationDownshift + xAxisBoxGap - 30})`);

    let currentXOffset = 0;
    xAxisBoxData.forEach(boxData => {
      const boxWidth = totalXAxisWidth * boxData.percentage;

      xAxisBoxesGroup.append("rect")
        .attr("x", currentXOffset)
        .attr("y", 0)
        .attr("width", boxWidth)
        .attr("height", xAxisBoxHeight)
        .style("fill", boxData.color)
        .style("stroke", "#B0B0B0");

      xAxisBoxesGroup.append("text")
        .attr("x", currentXOffset + boxWidth / 2)
        .attr("y", xAxisBoxHeight / 2)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("font-size", "12px") // Increased font size
        .style("font-weight", "600")
        .style("fill", boxData.tcolor)
        .text(boxData.text);

      currentXOffset += boxWidth;
    });

  }, [filteredMaturityLevels, containerWidth]);

  // Resize observer to update container width
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width || 1200);
      }
    };

    // Initial width
    updateWidth();

    // Create resize observer
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Helper function for text wrapping inside the legend squares (from Vue component)
  const wrapLegendText = (textSelection: any, maxWidth: number, itemRectHeight: number) => {
    const lineHeightEms = 1.1; // Line height in ems
    const safetyBuffer = 3; // Increased buffer to prevent text from touching edges

    textSelection.each(function(this: any) {
      const text = d3.select(this);
      const words = text.text().split(/\s+/).reverse();
      let word;
      let line: string[] = [];
      
      const x = text.attr("x"); // x is already set with left padding
      // Ensure availableWidth accounts for safety buffer to prevent overflow
      // Use a more conservative width to ensure text never overflows
      const availableWidth = Math.max(10, maxWidth - safetyBuffer);
      
      text.text(null); // Clear original text
      
      const tspans: any[] = [];
      // The first tspan's dy will be set later for vertical centering.
      // For now, let it be relative to the text element's y.
      let currentTspan = text.append("tspan").attr("x", x); 
      tspans.push(currentTspan);

      while ((word = words.pop()) !== undefined) {
        line.push(word);
        currentTspan.text(line.join(" "));
        const node = currentTspan.node();
        if (node) {
          const textLength = node.getComputedTextLength();
          // More aggressive wrapping - wrap if text exceeds available width
          if (textLength > availableWidth) {
            if (line.length > 1) {
              // Remove last word and wrap to next line
              line.pop();
              currentTspan.text(line.join(" "));
              line = [word];
              currentTspan = text.append("tspan").attr("x", x).attr("dy", `${lineHeightEms}em`).text(word);
              tspans.push(currentTspan);
            } else {
              // Single word is too long - force wrap anyway
              currentTspan.text(line.join(" "));
              line = [];
            }
          }
        }
      }

      // Vertical centering of the text block
      const numLines = tspans.length;
      const textNode = text.node();
      if (textNode) {
        const fontSizePx = parseFloat(window.getComputedStyle(textNode).fontSize);
        const totalTextHeightPx = numLines * lineHeightEms * fontSizePx;
        
        // The <text> element's y attribute is the center of the rect.
        // Adjust dy of the first tspan to center the block.
        const firstTspanDyPx = -(totalTextHeightPx / 2) + (lineHeightEms * fontSizePx / 2);
        if (tspans.length > 0) {
          tspans[0].attr("dy", `${firstTspanDyPx}px`);
        }
      }
    });
  };

  return (
    <div className={`relative w-full border rounded-lg shadow-sm overflow-hidden ${compactPresentation ? 'p-2' : 'p-4'}`}>
      <div className={`flex justify-between items-center ${compactPresentation ? 'mb-2' : 'mb-4'}`}>
        <h2 className="text-2xl font-bold text-left text-[#0F2A4F]">
          {customTitle ?? 'Maturity Journey'}
        </h2>
        {headerAction && filteredMaturityLevels.length > 0 ? headerAction : null}
      </div>
      {!hideDescription && (
        <p className='font-normal text-zinc-500 mb-4'>This maturity journey visualization shows the 28-step transformation path from foundation to advanced operations. The visualization tracks progress across the transformation journey and highlights the path to achieving advanced capabilities.</p>
      )}

      {/* Maturity Plan Dropdown */}
      {!hideDropdown && (
      <div className="mb-4">
        <label htmlFor="maturityPlan" className="block text-sm font-medium text-gray-700 mb-2">
          Choose A Plan
        </label>
        <div className="relative w-64">
        <select
          id="maturityPlan"
          value={effectivePlanId}
          onChange={(e) => setInternalSelectedPlan(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-md appearance-none bg-white pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
            {MATURITY_PLAN_OPTIONS.map((option) => (
              <option key={option.plan_id} value={option.plan_id}>
                {option.name}
              </option>
            ))}
          </select>
          <div className="absolute arrow-scale-down inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      )}

      {/* Chart container - content fits within container */}
      <div ref={containerRef} className="w-full overflow-hidden">
        {effectivePlanId === MATURITY_TRANSFORMATION_ID.NO_MATURITY ? (
          <div className="text-center py-8 text-gray-500">
            <p>Please select a maturity plan to view the maturity journey visualization.</p>
          </div>
        ) : customerId && dashboardLoading ? (
          <div className="flex justify-center py-10 items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]" />
          </div>
        ) : filteredMaturityLevels.length === 0 && effectivePlanId !== MATURITY_TRANSFORMATION_ID.NO_MATURITY ? (
          <div id="maturity-journey-graph" className="flex flex-col items-center justify-center py-12">
            <div className="text-center text-gray-500 max-w-md">
              <p className="text-lg font-medium mb-2">No data available</p>
              <p className="text-sm">Data will appear once the maturity journey is configured.</p>
            </div>
          </div>
        ) : (
          <div id="maturity-journey-graph">
            <svg ref={svgRef} style={{ width: '100%', maxWidth: '100%', height: 'auto' }}></svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransformationMaturityGraph;